import { KeyboardEvent, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { CouponInfo } from '../../../../models';
import { fromCoupon, fromOrder, useAppDispatch } from '../../../../store';
import { formatCurrency } from '../../../../components';
import { DEFAULT_CURRENCY_FORMAT_OPTIONS } from '../../../../utils/Constants';

const useStyles = makeStyles(theme => ({
  inputRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.75),
  },
  input: {
    flex: 1,
    minWidth: 0,
    height: 36,
    border: '1px solid #e6e4ef',
    borderRadius: 9,
    padding: '0 12px',
    fontSize: 13,
    fontFamily: 'inherit',
    color: '#16141d',
    backgroundColor: '#fff',
    outline: 'none',
    '&::placeholder': {
      color: '#9a98ad',
    },
    '&:focus': {
      borderColor: '#0f8461',
      boxShadow: '0 0 0 3px rgba(15,132,97,.15)',
    },
    '&:disabled': {
      backgroundColor: '#fafafd',
    },
  },
  applyButton: {
    flex: 'none',
    height: 36,
    padding: '0 15px',
    border: '1px solid #e6e4ef',
    borderRadius: 9,
    backgroundColor: '#fff',
    color: '#16141D',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#f4f3f7',
    },
    '&:disabled': {
      color: '#b5b3c4',
      cursor: 'default',
      backgroundColor: '#fff',
    },
  },
  appliedRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.75),
    padding: '9px 13px',
    backgroundColor: '#e7f8f1',
    border: '1px solid #b6ebd6',
    borderRadius: 9,
  },
  appliedLabel: {
    fontSize: 12.5,
    fontWeight: 700,
    color: '#0f8461',
  },
  removeButton: {
    background: 'none',
    border: 'none',
    padding: 0,
    color: '#0f8461',
    fontSize: 12,
    fontWeight: 600,
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  errorText: {
    marginTop: theme.spacing(0.75),
    fontSize: 12.5,
    lineHeight: 1.45,
    color: '#d24720',
  },
  addLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minHeight: 32,
    marginTop: theme.spacing(0.5),
    padding: 0,
    border: 'none',
    background: 'none',
    font: 'inherit',
    fontSize: 13,
    fontWeight: 600,
    color: '#3c4642',
    cursor: 'pointer',
    textAlign: 'left',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const PlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" aria-hidden="true">
    <path d="M6 12h12M12 6v12" />
  </svg>
);

export const formatCouponDiscount = (coupon: CouponInfo, intl: IntlShape): string => {
  if (coupon.discountType === 'PERCENT') {
    return `${coupon.discountAmount}%`;
  }
  return formatCurrency(intl, coupon.appliedAmount, DEFAULT_CURRENCY_FORMAT_OPTIONS);
};

export const OrderSummaryCoupon = ({ isAnnual }: { isAnnual: boolean }) => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [inputValue, setInputValue] = useState('');
  // Design: the coupon field stays folded behind "+ Add coupon" until the customer asks for it.
  const [open, setOpen] = useState(false);
  const previewLoading = useSelector(fromOrder.selectPreviewLoading);
  const appliedCoupon = useSelector(fromOrder.selectAppliedCoupon);
  const couponError = useSelector(fromOrder.selectCouponError);
  const couponInfo = useSelector(fromCoupon.selectCoupon);
  const couponRule = couponInfo?.rule;

  const handleApply = () => {
    const trimmed = inputValue.trim();
    if (trimmed) {
      // usePreviewTrue watches couponName and re-runs the preview with it.
      dispatch(fromOrder.setCouponName(trimmed));
      dispatch(fromCoupon.doFetchCouponByCode(trimmed));
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleApply();
    }
  };

  const handleRemove = () => {
    setInputValue('');
    setOpen(false);
    dispatch(fromOrder.clearCoupon());
  };

  if (appliedCoupon) {
    return (
      <Box id="order-summary-coupon-applied" className={classes.appliedRow}>
        <Typography className={classes.appliedLabel}>
          <FormattedMessage
            id="page.plan.v2.order.summary.coupon.applied"
            values={{
              code: appliedCoupon.code,
              discount: formatCouponDiscount(appliedCoupon, intl),
            }}
          />
        </Typography>
        <button
          type="button"
          className={classes.removeButton}
          onClick={handleRemove}
        >
          <FormattedMessage id="page.plan.v2.order.summary.coupon.remove" />
        </button>
      </Box>
    );
  }

  if (!open && !couponError) {
    return (
      <Box id="order-summary-coupon">
        <button
          type="button"
          id="order-summary-coupon-add"
          className={classes.addLink}
          aria-expanded={false}
          onClick={() => setOpen(true)}
        >
          <PlusIcon />
          <FormattedMessage id="page.plan.v2.order.summary.coupon.add" />
        </button>
      </Box>
    );
  }

  return (
    <Box id="order-summary-coupon">
      <Box className={classes.inputRow}>
        <input
          className={classes.input}
          placeholder={intl.formatMessage({
            id: 'page.plan.v2.order.summary.coupon.placeholder',
          })}
          value={inputValue}
          onChange={event => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={previewLoading}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={open}
        />
        <button
          type="button"
          className={classes.applyButton}
          onClick={handleApply}
          disabled={previewLoading || !inputValue.trim()}
        >
          {previewLoading
            ? <FormattedMessage id="order.confirmation.coupon.validating" />
            : <FormattedMessage id="order.confirmation.coupon.apply" />}
        </button>
      </Box>
      {couponError && (
        <Typography className={classes.errorText}>
          <FormattedMessage
            id={couponError}
            defaultMessage={intl.formatMessage({ id: 'coupon.code.invalid' })}
            values={{
              quota: couponRule?.minLicenses,
              amount: couponRule?.minAmount,
              cycle: isAnnual ? 'monthly' : 'annual',
            }}
          />
        </Typography>
      )}
    </Box>
  );
};
